import { FC, useMemo, useState, useEffect } from 'react';
import { useStore } from 'hooks/useRootStore';
import { observer } from 'mobx-react-lite';
import { useStyles } from './styles';
import {
  Button,
  Paper,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  FormControl,
  Select,
  Grid,
  IconButton,
} from '@material-ui/core';
import ControlPointIcon from '@material-ui/icons/ControlPoint';
import InfoIcon from '@material-ui/icons/Info';
import WebAssetIcon from '@material-ui/icons/WebAsset';
import CloseIcon from '@material-ui/icons/Close';
import { NewDataSourcePopup } from 'components/PopUps/NewDataSourcePopup';
import { useQuery, useMutation } from 'react-query';
import Calender from './../../../../assets/icons/EndUser/Calender.svg';
import { DataGrid, GridCellParams, GridColumns } from '@material-ui/data-grid';
import { getRowsPerPageOptions } from 'utils/pagination';
import { toFormatDateTime } from 'utils';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import TabPanel from 'components/TabPanel';
import { getFieldConfig } from 'utils/fieldConfigUtils';

// import { useAppPreloader } from 'hooks/useAppPreloader';
export const DataSources: FC = observer(() => {
  const [openDataSourcePopup, setOpenDataSourcePopup] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [tabValue, setTabValue] = useState('one');
  const [attributeMappings, setAttributeMappings] = useState<{ [key: string]: { [key: string]: string } }>({
    people: {},
    group: {},
    department: {},
  });
  const handleClickAwayAdd = () => {
    setOpenAdd(false);
  };
  const handleClickAdd = () => {
    setOpenAdd((prev) => !prev);
  };
  const handleClick = () => {
    setOpenDataSourcePopup(true);
    setOpenAdd(false);
  };
  const {
    importStore: { ldapAllConnections, ldapGetConnectOptions, getLdapAttributePreferences },
    AppStore: { AttributePreferences },
    // ldapStore: { getSync, syncs },
    notificationStore: { openNotification },
  } = useStore();
  const classes = useStyles();
  const ldapGet = useQuery('getUsersReq', () => ldapGetConnectOptions(), {
    onError: () => openNotification('error', `Can't get Ldap Data`),
  });

  const getLdapAttributePreferencesReq = useQuery(
    'getLdapAttributePreferencesReq',
    () => getLdapAttributePreferences(),
    {
      onError: () => openNotification('error', `Can't get attribute`),
    }
  );

  const infoData = getLdapAttributePreferencesReq?.data?.data;

  const getAttributesByEntity = (entity: string) => {
    if (!infoData?.attributes) return [];
    return infoData.attributes.filter((attr: any) => attr.entity === entity);
  };

  const getLdapAttributesByEntity = (entity: string) => {
    if (!infoData?.attributes) return [];
    return infoData.attributes
      .filter((attr: any) => attr.entity === entity)
      .map((attr: any) => {
        const fieldConfig = getFieldConfig(attr);
        return {
          ...attr,
          destKey: fieldConfig.destKey,
          destLabel: fieldConfig.destLabel,
          sourseKey: fieldConfig.sourseKey || fieldConfig.sourceKey,
          sourseLabel: fieldConfig.sourseLabel || fieldConfig.sourceLabel,
          isFromPreference: fieldConfig.isFromPreference,
        };
      })
      .filter((attr: any) => attr.sourseKey);
  };

  const getCurrentTabMappings = () => {
    if (tabValue === 'one') return attributeMappings.people || {};
    if (tabValue === 'two') return attributeMappings.group || {};
    if (tabValue === 'three') return attributeMappings.department || {};
    return {};
  };

  const handleAttributeMapping = (destKey: string, sourseKey: string) => {
    const tabKey = tabValue === 'one' ? 'people' : tabValue === 'two' ? 'group' : 'department';
    setAttributeMappings((prev) => {
      const newMappings = { ...prev };

      if (sourseKey && sourseKey !== 'none') {
        Object.keys(newMappings[tabKey]).forEach((key) => {
          if (key !== destKey && newMappings[tabKey][key] === sourseKey) {
            newMappings[tabKey][key] = '';
          }
        });
      }

      newMappings[tabKey] = {
        ...newMappings[tabKey],
        [destKey]: sourseKey === 'none' ? '' : sourseKey,
      };
      return newMappings;
    });
  };

  const LdapAttributePreferencesReq = useMutation((data: any) => AttributePreferences(data), {
    mutationKey: 'LdapAttributePreferencesReq',
    onSuccess: (data) => {
      openNotification('success', 'Attribute mappings saved!');
      getLdapAttributePreferencesReq.refetch();
      setAttributeMappings({
        people: {},
        group: {},
        department: {},
      });
    },
    onError: (error) => {
      openNotification('error', 'Failed to save attribute mappings');
    },
  });

  const isLdapAttributeFixed = (sourseKey: string) => {
    const fixedLdapFields = [
      'mail',
      'email',
      'Email',
      'userPassword',
      'password',
      'Password',
      'uid',
      'userId',
      'UserId',
      'passwordMethod',
      'PasswordMethod',
    ];
    return fixedLdapFields.includes(sourseKey);
  };

  const isAttributeEditable = (destKey: string) => {
    return true;
  };

  const handleNext = () => {
    const currentEntity = tabValue === 'one' ? 'people' : tabValue === 'two' ? 'group' : 'department';
    const requiredFields = getAttributesByEntity(currentEntity)
      .filter((attr: any) => attr.required)
      .filter((attr: any) => {
        const fieldConfig = getFieldConfig(attr);
        if (!fieldConfig.destKey) return false;
        return !getCurrentTabMappings()[fieldConfig.destKey];
      });

    if (requiredFields.length > 0) {
      const fieldNames = requiredFields
        .map((field: any) => {
          const fieldConfig = getFieldConfig(field);
          return fieldConfig.destLabel || 'Unknown';
        })
        .join(', ');
      openNotification('error', `Please map required fields: ${fieldNames}`);
      return;
    }

    if (tabValue === 'one') setTabValue('two');
    else if (tabValue === 'two') setTabValue('three');
  };

  const handleBack = () => {
    if (tabValue === 'three') setTabValue('two');
    else if (tabValue === 'two') setTabValue('one');
  };

  const isLastTab = tabValue === 'three';
  const isFirstTab = tabValue === 'one';

  const handleSave = () => {
    const payload = {
      app_name: 'ldap',
      attributes: [] as any[],
    };

    const allMappings = {
      people: attributeMappings.people || {},
      group: attributeMappings.group || {},
      department: attributeMappings.department || {},
    };

    Object.entries(allMappings).forEach(([entity, mappings]) => {
      Object.entries(mappings).forEach(([destKey, sourseKey]) => {
        if (sourseKey) {
          const originalAttr = getAttributesByEntity(entity).find((attr: any) => {
            const fieldConfig = getFieldConfig(attr);
            return fieldConfig.destKey === destKey;
          });

          if (originalAttr) {
            const fieldConfig = getFieldConfig(originalAttr);
            const selectedLdapAttr = getLdapAttributesByEntity(entity).find(
              (attr: any) => attr.sourseKey === sourseKey
            );

            const attributeItem = {
              type: originalAttr.type,
              entity,
              hidden: originalAttr.hidden,
              required: originalAttr.required,
              default: originalAttr.default,
              preference: {
                destKey,
                destLabel: fieldConfig.destLabel || '',
                sourseKey,
                sourseLabel: selectedLdapAttr?.sourseLabel || sourseKey,
              },
            };

            payload.attributes.push(attributeItem);
          }
        }
      });
    });

    LdapAttributePreferencesReq.mutate(payload);
    setOpenInfoModal(false);
  };

  useEffect(() => {
    if (infoData?.attributes) {
      const mappings = {
        people: {} as { [key: string]: string },
        group: {} as { [key: string]: string },
        department: {} as { [key: string]: string },
      };

      infoData.attributes.forEach((attr: any) => {
        const fieldConfig = getFieldConfig(attr);
        if (fieldConfig.destKey) {
          mappings[attr.entity as keyof typeof mappings][fieldConfig.destKey] =
            fieldConfig.sourseKey || fieldConfig.sourceKey || '';
        }
      });

      setAttributeMappings(mappings);
    }
  }, [infoData]);

  const rows = useMemo(() => {
    return !ldapGet.isFetching ? ldapAllConnections : [];
  }, [ldapGet.isFetching, ldapAllConnections]);
  const [pageSize, setPageSize] = useState(10);
  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);
  const columns: GridColumns = [
    {
      field: 'icon',
      headerName: ' ',
      width: 40,
      align: 'center',
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => <CloudUploadIcon style={{ color: 'grey' }} />,
    },
    {
      field: 'createdAt',
      headerName: 'DATE AND TIME',
      width: 250,
      renderCell: (params: GridCellParams) => (
        <div style={{ display: 'flex', gap: '10px' }}>
          <img src={Calender} alt='' />
          {toFormatDateTime(params.row.createdAt.toString())}
        </div>
      ),
    },
    { field: 'type', headerName: 'FILE TYPE', width: 200 },
    { field: 'name', headerName: 'FILE NAME', width: 400 },
  ];
  // useAppPreloader(getSyncReq.isFetching);
  return (
    <>
      <div className={classes.content}>
        <div style={{ textAlign: 'center' }}>You can upload existing user accounts using a CSV or LDIF file</div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div className={classes.root}>
            <Button
              className={classes.btnAdd}
              variant='contained'
              color='secondary'
              onClick={() => setOpenInfoModal(true)}
            >
              <InfoIcon />
            </Button>
          </div>

          <ClickAwayListener onClickAway={handleClickAwayAdd}>
            <div className={classes.root}>
              <Button variant='contained' color='secondary' className={classes.btnAdd} onClick={handleClickAdd}>
                <ControlPointIcon />
              </Button>
              {openAdd ? (
                <div className={classes.dropdown}>
                  {/* {mockCreateActions.map((item, index) => ( */}
                  <MenuItem className={classes.selectItem} onClick={handleClick}>
                    <CloudUploadIcon /> Upload Accounts
                  </MenuItem>
                  {/* ))} */}
                </div>
              ) : null}
            </div>
          </ClickAwayListener>
        </div>
      </div>

      {/* <div>
        {syncs.some((item) => item.people === 0 || item.department === 0 || item.group === 0) ? (
          <h1 className={classes.sync}> Sync in Progress</h1>
        ) : null}
      </div> */}
      <Paper className={classes.dataGrid} elevation={0}>
        <DataGrid
          className={classes.noBorder}
          autoHeight
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          rowsPerPageOptions={rowsPerPageOptions}
          pagination
          onPageChange={(params) => setPageSize(params.pageSize)}
        />
      </Paper>
      <NewDataSourcePopup
        open={openDataSourcePopup}
        handleClose={() => {
          setOpenDataSourcePopup(false);
        }}
      />

      <Dialog open={openInfoModal} onClose={() => setOpenInfoModal(false)} maxWidth='lg' fullWidth>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
          <DialogTitle>Configure Attribute Mapping</DialogTitle>
          <IconButton onClick={() => setOpenInfoModal(false)}>
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            className={classes.tabs}
            TabIndicatorProps={{ style: { color: 'transparent', background: 'transparent' } }}
            // TabIndicatorProps={{ style: { backgroundColor: '#1976d2' } }}
          >
            <Tab value='one' label='Users' className={classes.label} />
            <Tab value='two' label='Groups' className={classes.label} />
            <Tab value='three' label='Departments' className={classes.label} />
          </Tabs>

          <TabPanel value={tabValue} index='one'>
            <TableContainer>
              <Table aria-label='user attribute mapping table'>
                <TableHead>
                  <TableRow>
                    <TableCell>LDAP Attributes</TableCell>
                    <TableCell>Source Attributes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getAttributesByEntity('people')
                    .map((attr: any, index: number) => {
                      const fieldConfig = getFieldConfig(attr);
                      // Show row if it has either destKey or sourseKey (for display purposes)
                      if (!fieldConfig.destKey && !fieldConfig.sourseKey && !fieldConfig.sourceKey) return null;

                      return (
                        <TableRow key={index}>
                          <TableCell>
                            {fieldConfig.destLabel || 'Unknown'}
                            {attr.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                          </TableCell>
                          <TableCell>
                            {isLdapAttributeFixed(fieldConfig.sourseKey || fieldConfig.sourceKey || '') ? (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>
                                {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                              </span>
                            ) : isAttributeEditable(fieldConfig.destKey || '') ? (
                              <FormControl fullWidth>
                                <Select
                                  value={getCurrentTabMappings()[fieldConfig.destKey || ''] || ''}
                                  onChange={(e) =>
                                    handleAttributeMapping(fieldConfig.destKey || '', e.target.value as string)
                                  }
                                  displayEmpty
                                >
                                  <MenuItem value='none'>None</MenuItem>
                                  {getLdapAttributesByEntity('people').map((option: any, optIndex: number) => (
                                    <MenuItem
                                      key={optIndex}
                                      value={option.sourseKey}
                                      disabled={isLdapAttributeFixed(option.sourseKey)}
                                    >
                                      <WebAssetIcon style={{ marginRight: 8 }} />
                                      {option.sourseLabel}
                                      {isLdapAttributeFixed(option.sourseKey) && ' (Fixed)'}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>
                                {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                    .filter(Boolean)}
                </TableBody>
              </Table>
            </TableContainer>
            <Grid container justify='space-between' style={{ marginTop: 20 }}>
              {!isFirstTab && (
                <Button variant='outlined' onClick={handleBack}>
                  Back
                </Button>
              )}
              <div style={{ marginLeft: 'auto' }}>
                {isLastTab ? (
                  <Button variant='contained' color='primary' onClick={handleSave}>
                    Save Mappings
                  </Button>
                ) : (
                  <Button variant='contained' color='primary' onClick={handleNext}>
                    Next
                  </Button>
                )}
              </div>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index='two'>
            <TableContainer>
              <Table aria-label='group attribute mapping table'>
                <TableHead>
                  <TableRow>
                    <TableCell>LDAP Attributes</TableCell>
                    <TableCell>Source Attributes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getAttributesByEntity('group')
                    .map((attr: any, index: number) => {
                      const fieldConfig = getFieldConfig(attr);
                      // Show row if it has either destKey or sourseKey (for display purposes)
                      if (!fieldConfig.destKey && !fieldConfig.sourseKey && !fieldConfig.sourceKey) return null;

                      return (
                        <TableRow key={index}>
                          <TableCell>
                            {fieldConfig.destLabel || 'Unknown'}
                            {attr.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                          </TableCell>
                          <TableCell>
                            {isLdapAttributeFixed(fieldConfig.sourseKey || fieldConfig.sourceKey || '') ? (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>
                                {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                              </span>
                            ) : isAttributeEditable(fieldConfig.destKey || '') ? (
                              <FormControl fullWidth>
                                <Select
                                  value={getCurrentTabMappings()[fieldConfig.destKey || ''] || ''}
                                  onChange={(e) =>
                                    handleAttributeMapping(fieldConfig.destKey || '', e.target.value as string)
                                  }
                                  displayEmpty
                                >
                                  <MenuItem value='none'>None</MenuItem>
                                  {getLdapAttributesByEntity('group').map((option: any, optIndex: number) => (
                                    <MenuItem
                                      key={optIndex}
                                      value={option.sourseKey}
                                      disabled={isLdapAttributeFixed(option.sourseKey)}
                                    >
                                      <WebAssetIcon style={{ marginRight: 8 }} />
                                      {option.sourseLabel}
                                      {isLdapAttributeFixed(option.sourseKey) && ' (Fixed)'}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>
                                {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                    .filter(Boolean)}
                </TableBody>
              </Table>
            </TableContainer>
            <Grid container justify='space-between' style={{ marginTop: 20 }}>
              {!isFirstTab && (
                <Button variant='outlined' onClick={handleBack}>
                  Back
                </Button>
              )}
              <div style={{ marginLeft: 'auto' }}>
                {isLastTab ? (
                  <Button variant='contained' color='primary' onClick={handleSave}>
                    Save Mappings
                  </Button>
                ) : (
                  <Button variant='contained' color='primary' onClick={handleNext}>
                    Next
                  </Button>
                )}
              </div>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index='three'>
            <TableContainer>
              <Table aria-label='department attribute mapping table'>
                <TableHead>
                  <TableRow>
                    <TableCell>LDAP Attributes</TableCell>
                    <TableCell>Source Attributes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getAttributesByEntity('department')
                    .map((attr: any, index: number) => {
                      const fieldConfig = getFieldConfig(attr);
                      // Show row if it has either destKey or sourseKey (for display purposes)
                      if (!fieldConfig.destKey && !fieldConfig.sourseKey && !fieldConfig.sourceKey) return null;

                      return (
                        <TableRow key={index}>
                          <TableCell>
                            {fieldConfig.destLabel || 'Unknown'}
                            {attr.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                          </TableCell>
                          <TableCell>
                            {isLdapAttributeFixed(fieldConfig.sourseKey || fieldConfig.sourceKey || '') ? (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>
                                {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                              </span>
                            ) : isAttributeEditable(fieldConfig.destKey || '') ? (
                              <FormControl fullWidth>
                                <Select
                                  value={getCurrentTabMappings()[fieldConfig.destKey || ''] || ''}
                                  onChange={(e) =>
                                    handleAttributeMapping(fieldConfig.destKey || '', e.target.value as string)
                                  }
                                  displayEmpty
                                >
                                  <MenuItem value='none'>None</MenuItem>
                                  {getLdapAttributesByEntity('department').map((option: any, optIndex: number) => (
                                    <MenuItem
                                      key={optIndex}
                                      value={option.sourseKey}
                                      disabled={isLdapAttributeFixed(option.sourseKey)}
                                    >
                                      <WebAssetIcon style={{ marginRight: 8 }} />
                                      {option.sourseLabel}
                                      {isLdapAttributeFixed(option.sourseKey) && ' (Fixed)'}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>
                                {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                    .filter(Boolean)}
                </TableBody>
              </Table>
            </TableContainer>
            <Grid container justify='space-between' style={{ marginTop: 20 }}>
              {!isFirstTab && (
                <Button variant='outlined' onClick={handleBack}>
                  Back
                </Button>
              )}
              <div style={{ marginLeft: 'auto' }}>
                {isLastTab ? (
                  <Button variant='contained' color='primary' onClick={handleSave}>
                    Save Mappings
                  </Button>
                ) : (
                  <Button variant='contained' color='primary' onClick={handleNext}>
                    Next
                  </Button>
                )}
              </div>
            </Grid>
          </TabPanel>
        </DialogContent>
      </Dialog>
    </>
  );
});
